import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'Is SCHD a better dividend ETF than VOO?',
    a: 'They are different tools. SCHD is a dividend-focused ETF — it filters for sustainability of payouts and dividend growth. VOO tracks the entire S&P 500, which has a much lower yield. SCHD wins on income; VOO wins on total long-term return because it includes high-growth non-payers (Amazon, Tesla, Meta). If your goal is dividend income, SCHD; if it is "the market," VOO.',
  },
  {
    q: 'What is SCHD\'s yield vs VOO\'s yield?',
    a: 'SCHD typically yields around 3.5–4.0%. VOO typically yields around 1.2–1.5%. Roughly 2.5–3× the yield, in exchange for sector concentration and lower exposure to high-growth tech names.',
  },
  {
    q: 'Can I own both SCHD and VOO?',
    a: 'Yes — many investors hold both. SCHD covers the dividend-income sleeve; VOO covers the broad-market growth sleeve. A common split is 30–50% SCHD, the rest VOO, depending on how much income you need today vs growth you want for tomorrow.',
  },
  {
    q: 'Does SCHD outperform VOO long-term?',
    a: 'Over the last decade, total return (price + dividends, reinvested): VOO has narrowly outperformed SCHD because tech mega-caps lifted the S&P 500 dramatically. SCHD has lower drawdowns and a smoother ride, so on a risk-adjusted basis the two are closer than the headline number suggests.',
  },
  {
    q: 'Which has lower fees, SCHD or VOO?',
    a: 'VOO charges 0.03% expense ratio; SCHD charges 0.06%. Both are dirt cheap; the 0.03% gap on a $100,000 holding is $30 a year — usually a rounding error vs the income difference.',
  },
];

export default function LearnSchdVsVoo() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="SCHD vs VOO — Dividend ETF vs S&P 500 ETF (2026) | DiviDrip"
      description="SCHD vs VOO comparison: yield, holdings, performance, fees. Which is better for dividend income — and when owning both makes sense."
      canonical="https://twylightcrow.com/learn/schd-vs-voo"
      guidePath="/learn/schd-vs-voo"
      sectionLabel="Dividend Tiers & ETF Face-offs"
      sectionAnchor="tiers"
      kicker="Learn · ETF face-off"
      h1="SCHD vs VOO — Dividend ETF or S&P 500 ETF?"
      datePublished="2026-05-22"
      jsonLd={jsonLd}
      related={[
        { to: '/true-return/schd-vs-voo', label: 'True Return Chart — SCHD vs VOO, $10K after dividends and inflation' },
        { to: '/learn/boring-is-beautiful', label: 'Why dividend growers beat glamour stocks over 30 years' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — a starter portfolio' },
        { to: '/learn/yield-on-cost', label: 'Yield on cost — your real long-term yield' },
        { to: '/learn/drip-vs-cash-dividends', label: 'DRIP vs cash dividends — which to choose?' },
        { to: '/learn/jepi-vs-jepq', label: 'JEPI vs JEPQ — covered-call yield face-off' },
        { to: '/learn/what-is-drip', label: 'What is DRIP? Reinvestment explained' },
        { to: '/learn/dividend-kings-vs-aristocrats-vs-achievers', label: 'Dividend Kings vs Aristocrats vs Achievers' },
        { to: '/screener', label: 'Stock Screener — filter ETFs by yield, sector' },
      ]}
    >
      <p>
        Two giants of the ETF world. <strong>SCHD</strong> (Schwab U.S. Dividend Equity ETF)
        and <strong>VOO</strong> (Vanguard S&amp;P 500 ETF) both own large-cap US stocks,
        both are cheap, both are widely held — but they answer two completely different
        questions. SCHD asks: <em>which dividend-paying companies will keep paying?</em>
        VOO asks: <em>what does the whole US large-cap market look like?</em> The answer
        you need depends on what your portfolio is for.
      </p>

      <h2>Side-by-side</h2>
      <table className="learn-table">
        <thead>
          <tr>
            <th className="text-left font-semibold">Metric</th>
            <th className="text-left font-semibold">SCHD</th>
            <th className="text-left font-semibold">VOO</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="font-semibold">Tracks</td>
            <td>Dow Jones U.S. Dividend 100 Index</td>
            <td>S&amp;P 500 Index</td>
          </tr>
          <tr>
            <td className="font-semibold">Holdings</td>
            <td>~100 dividend payers</td>
            <td>~500 large caps</td>
          </tr>
          <tr>
            <td className="font-semibold">Yield (approx)</td>
            <td className="font-mono">3.5 – 4.0%</td>
            <td className="font-mono">1.2 – 1.5%</td>
          </tr>
          <tr>
            <td className="font-semibold">Expense ratio</td>
            <td className="font-mono">0.06%</td>
            <td className="font-mono">0.03%</td>
          </tr>
          <tr>
            <td className="font-semibold">Top sector tilt</td>
            <td>Financials, Industrials, Consumer Staples</td>
            <td>Technology (28%+)</td>
          </tr>
          <tr>
            <td className="font-semibold">Tech mega-cap weight</td>
            <td className="text-rose-600 dark:text-rose-400 font-semibold">Low</td>
            <td className="text-emerald-600 dark:text-emerald-400 font-semibold">High</td>
          </tr>
        </tbody>
      </table>

      <h2>Why VOO wins on long-term total return (usually)</h2>
      <p>
        VOO holds Apple, Microsoft, Nvidia, Amazon, Meta, Tesla, Alphabet — the companies
        that have driven the bulk of the last 15 years of US equity returns. SCHD by design
        excludes most of them because they either don&apos;t pay dividends (Amazon, Meta, Tesla)
        or fail SCHD&apos;s dividend-growth screen. The result: VOO has handily outperformed
        SCHD on a price basis over 5- and 10-year windows.
      </p>

      <h2>Why SCHD wins on income (always) and drawdowns (usually)</h2>
      <p>
        SCHD pays roughly 3× the yield of VOO and writes cheques quarterly. Because SCHD
        skews toward conservative balance sheets and slower-growth sectors, it falls less
        in bear markets — in 2022 SCHD was down ~3% while VOO was down ~18%. For investors
        nearer retirement, that smaller drawdown is the whole point.
      </p>

      <h2>When to choose SCHD</h2>
      <ul>
        <li>You want a meaningful, growing dividend stream now or in the next 5 years.</li>
        <li>You already have growth exposure elsewhere (individual tech names, QQQ, VTI).</li>
        <li>You value lower drawdowns more than maximum compound return.</li>
        <li>You&apos;re building a retirement-income portfolio.</li>
      </ul>

      <h2>When to choose VOO</h2>
      <ul>
        <li>You want the &ldquo;market return&rdquo; benchmark exposure.</li>
        <li>You&apos;re young (20s/30s) and can absorb deeper drawdowns for higher long-run growth.</li>
        <li>You don&apos;t need current income; you reinvest everything.</li>
        <li>You want the simplest possible one-fund core portfolio.</li>
      </ul>

      <h2>Owning both</h2>
      <p>
        The most common split among DiviDrip users who hold both: <strong>60% VOO + 40% SCHD</strong> for
        a 30-year-old still accumulating; <strong>30% VOO + 70% SCHD</strong> for a 60-year-old
        starting to lean on income. A 50/50 split is also defensible. There is no wrong
        answer — both are excellent core holdings.
      </p>

      <h2>Compare them live</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link>, search <strong>SCHD</strong>, open the Stock Modal,
        tap <em>Compare</em>, and pick VOO. You&apos;ll see 36 months of monthly yield
        overlaid with crossover diamonds on the dates SCHD&apos;s yield jumped above (or
        below) VOO&apos;s. Click <em>Triangle</em> on either ticker to plot Revenue × EPS ×
        Dividend growth — fundamentals telling you whether the dividend stream is healthy.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <p className="text-xs text-muted-foreground italic mt-6">
        Disclaimer: Not investment advice. Yields and sector weights drift over time;
        always check current numbers before buying. Past performance is not predictive.
      </p>
    </LearnPage>
  );
}
